import {
    areaValueForRow,
    type CoverageArea,
} from '@apps/web/features/coverage/model/area_model';
import {
    summarizeSlotAvailability,
    WORK_WEEK_START_INDEX,
} from '@apps/web/features/coverage/model/cell_model';
import {
    type CoverageRow,
    DAY_INDEXES,
    DAY_PARTS,
    type DayPartKey,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';

export { type CoverageArea } from '@apps/web/features/coverage/model/area_model';

export type CoverageColumn = {
    readonly group: 'weekday' | 'weekend';
    readonly dayPart: DayPartKey;
};

export type CoverageCell = {
    readonly column: CoverageColumn;
    /** Caregivers available or partial on at least one matching day. */
    readonly availableCount: number;
};

export type CoverageBucket = {
    readonly areaKey: string;
    readonly areaLabel: string;
    readonly rosterCount: number;
    readonly cells: ReadonlyArray<CoverageCell>;
};

export const COVERAGE_COLUMNS: ReadonlyArray<CoverageColumn> = [
    ...DAY_PARTS.map((part) => ({
        group: 'weekday' as const,
        dayPart: part.key,
    })),
    ...DAY_PARTS.map((part) => ({
        group: 'weekend' as const,
        dayPart: part.key,
    })),
];

export const LOW_COVERAGE_THRESHOLD = 2;

/**
 * Day indexes for a column group, in work-week grid coordinates. The
 * grid's day_index 0 is the org's work-week start day, so weekday /
 * weekend membership must be computed against the real weekday.
 */
export function columnDayIndexes(
    group: 'weekday' | 'weekend',
    workWeekStartDay: WorkWeekStartDay,
): Array<number> {
    const start = WORK_WEEK_START_INDEX[workWeekStartDay];
    return DAY_INDEXES.filter((dayIndex) => {
        const weekday = (start + dayIndex) % 7;
        const isWeekend = weekday === 5 || weekday === 6;
        return group === 'weekend' ? isWeekend : !isWeekend;
    });
}

/** Coverage counts derive from the currently filtered rows and selected cells. */
export function buildCoverage(
    rows: ReadonlyArray<CoverageRow>,
    area: CoverageArea,
    workWeekStartDay: WorkWeekStartDay,
): Array<CoverageBucket> {
    const groups = new Map<
        string,
        { readonly label: string; readonly rows: Array<CoverageRow> }
    >();
    for (const row of rows) {
        const areaValue = areaValueForRow(row, area);
        const bucket = groups.get(areaValue.key);
        if (bucket === undefined) {
            groups.set(areaValue.key, {
                label: areaValue.label,
                rows: [row],
            });
        } else {
            bucket.rows.push(row);
        }
    }

    const dayIndexesByGroup = {
        weekday: columnDayIndexes('weekday', workWeekStartDay),
        weekend: columnDayIndexes('weekend', workWeekStartDay),
    };
    const columnsWithSlots = COVERAGE_COLUMNS.map((column) => ({
        column,
        slots: dayIndexesByGroup[column.group].map(
            (dayIndex) => [dayIndex, column.dayPart] as const,
        ),
    }));

    return [...groups.entries()]
        .sort(
            ([areaKeyA, groupA], [areaKeyB, groupB]) =>
                groupA.label.localeCompare(groupB.label) ||
                areaKeyA.localeCompare(areaKeyB),
        )
        .map(([areaKey, group]) => ({
            areaKey,
            areaLabel: group.label,
            rosterCount: group.rows.length,
            cells: columnsWithSlots.map(({ column, slots }) => {
                let availableCount = 0;
                for (const row of group.rows) {
                    if (
                        summarizeSlotAvailability(row, slots)?.availability ===
                        'available'
                    ) {
                        availableCount += 1;
                    }
                }
                return { column, availableCount };
            }),
        }));
}

export function maxCoverageCount(
    buckets: ReadonlyArray<CoverageBucket>,
): number {
    let max = 1;
    for (const bucket of buckets) {
        for (const cell of bucket.cells) {
            max = Math.max(max, cell.availableCount);
        }
    }
    return max;
}
